import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
if (!STRIPE_PUBLISHABLE_KEY) {
  throw new Error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set — refusing to initialise Stripe with no key.')
}
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

function CheckoutForm({ booking, clientSecret, onSuccess }) {
const stripe = useStripe()
const elements = useElements()
const [processing, setProcessing] = useState(false)
const [error, setError] = useState('')

const handlePay = async () => {
if (!stripe || !elements) return
setProcessing(true)
setError('')
const result = await stripe.confirmCardPayment(clientSecret, {
payment_method: {
card: elements.getElement(CardElement),
}
})
if (result.error) {
setError(result.error.message)
setProcessing(false)
return
}

await supabase.from('bookings').update({
status: 'confirmed',
paid_at: new Date().toISOString(),
stripe_payment_intent_id: result.paymentIntent.id,
}).eq('id', booking.id)

try {
await supabase.from('messages').insert({
booking_id: booking.id,
sender_id: booking.hirer_id,
body: `💳 Payment authorised! $${booking.total_amount?.toFixed(2)} is held securely. Funds will be released to you once the item is returned. Please arrange a time to collect.`,
read: false,
})
} catch (e) {
console.log('Auto-message failed:', e)
}

try {
const { data: ownerEmail } = await supabase.rpc('get_user_email', { user_id: booking.owner_id })
if (ownerEmail) {
const { data: { session } } = await supabase.auth.getSession()
fetch(`${BACKEND_URL}/notify-payment`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
ownerEmail,
ownerName: booking.owner?.full_name || 'Owner',
hirerName: booking.hirer?.full_name || 'Your hirer',
itemTitle: booking.listings?.title || 'your item',
total: booking.total_amount?.toFixed(2),
})
}).catch(e => console.log('Payment email failed:', e))
}
} catch (e) {
console.log('Payment notify failed:', e)
}

onSuccess()
}

return (
<div>
<div style={{ background: 'var(--primary-pale)', border: '1px solid var(--primary)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--primary)' }}>
🛡️ <strong>Secure escrow:</strong> Your card will be authorised — not charged. Funds are only released to the owner once you confirm the item has been returned safely.
</div>

<div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '1rem', marginBottom: 16 }}>
<CardElement options={{
hidePostalCode: true,
style: {
base: {
fontSize: '16px',
color: '#333',
'::placeholder': { color: '#aaa' }
}
}
}} />
</div>
{error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
<button className="btn btn-gradient btn-full btn-lg" onClick={handlePay} disabled={processing || !stripe}>
{processing ? 'Authorising...' : `🔒 Authorise $${booking.total_amount?.toFixed(2)}`}
</button>
<p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
🔒 Secured by Stripe. Your card is held until item is returned. No charge made until release.
</p>
</div>
)
}

export default function Payment() {
const { user, loading: authLoading } = useAuth()
const navigate = useNavigate()
const [searchParams] = useSearchParams()
const [booking, setBooking] = useState(null)
const [loading, setLoading] = useState(true)
const [success, setSuccess] = useState(false)
const [error, setError] = useState('')

const bookingId = searchParams.get('booking')
const clientSecret = searchParams.get('secret')

useEffect(() => {
if (authLoading) return
if (!user) return // ProtectedRoute sends logged-out visitors to sign-in
if (!bookingId || !clientSecret) {
setError('Missing payment details in URL. Please go back to your booking and try again.')
setLoading(false)
return
}
fetchBooking()
}, [user, authLoading])

const fetchBooking = async () => {
try {
let { data, error: fetchErr } = await supabase
.from('bookings')
.select('*, listings(title, images)')
.eq('id', bookingId)
.single()
if (data) {
const _pids = [data.hirer_id, data.owner_id].filter(Boolean)
let _pmap = {}
if (_pids.length) {
const { data: _pp } = await supabase.from('public_profiles').select('id, full_name').in('id', _pids)
;(_pp || []).forEach(x => { _pmap[x.id] = x })
}
data = { ...data, hirer: _pmap[data.hirer_id] || null, owner: _pmap[data.owner_id] || null }
}
if (fetchErr) {
setError('Could not load booking: ' + fetchErr.message)
setLoading(false)
return
}
setBooking(data)
setLoading(false)
} catch (e) {
setError('Error loading booking: ' + e.message)
setLoading(false)
}
}

if (authLoading || loading) return <div><Header /><div className="spinner-wrap"><div className="spinner" /></div></div>

if (error) return (
<div>
<Header />
<div className="container" style={{ maxWidth: 520, padding: '2rem 1rem', textAlign: 'center' }}>
<div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
<h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Payment error</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>{error}</p>
<button className="btn btn-primary" onClick={() => navigate('/my-bookings')}>Back to bookings</button>
</div>
</div>
)

if (!booking) return (
<div>
<Header />
<div className="container" style={{ maxWidth: 520, padding: '2rem 1rem', textAlign: 'center' }}>
<div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
<h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Booking not found</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>This booking may have been removed or never existed.</p>
<button className="btn btn-primary" onClick={() => navigate('/my-bookings')}>Back to bookings</button>
</div>
</div>
)

if (success) return (
<div>
<Header />
<div className="container" style={{ maxWidth: 520, padding: '3rem 1rem', textAlign: 'center' }}>
<div style={{ fontSize: 64, marginBottom: 16 }}>🛡️</div>
<h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Card authorised!</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
$<strong>{booking.total_amount?.toFixed(2)}</strong> is held securely for <strong>{booking.listings?.title}</strong>.
</p>
<div style={{ background: 'var(--primary-pale)', borderRadius: 12, padding: '14px 16px', marginBottom: 24, fontSize: 13, color: 'var(--primary)', textAlign: 'left' }}>
<strong>What happens next:</strong>
<ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 1.7 }}>
<li>The owner has been notified</li>
<li>Arrange pickup via Messages</li>
<li>Upload pickup + return photos</li>
<li>Funds released to owner after you confirm return</li>
</ul>
</div>
<button className="btn btn-gradient" onClick={() => navigate('/my-bookings')}>
View my bookings
</button>
</div>
</div>
)

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 520, padding: '1.5rem 1rem' }}>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>← Back</button>
<h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: '1.5rem', color: 'var(--navy)' }}>Authorise payment</h1>

<div className="card card-body" style={{ marginBottom: '1rem' }}>
<div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--navy)' }}>{booking.listings?.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
Owner: {booking.owner?.full_name} · {booking.hours} hrs
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 6 }}>
<span>Hire amount</span>
<span style={{ fontWeight: 700 }}>${((booking.total_amount || 0) - (booking.deposit_amount || 0)).toFixed(2)}</span>
</div>
{booking.deposit_amount > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
<span>Security deposit (refundable)</span>
<span>${(booking.deposit_amount || 0).toFixed(2)}</span>
</div>
)}
<div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700 }}>
<span>Total authorised</span>
<span style={{ color: 'var(--primary)' }}>${booking.total_amount?.toFixed(2)}</span>
</div>
</div>

<div className="card card-body">
<div style={{ fontWeight: 600, marginBottom: 16, color: 'var(--navy)' }}>Card details</div>
{(() => {
const hireEnd = new Date(booking.start_date)
hireEnd.setDate(hireEnd.getDate() + Math.ceil((booking.hours || 0) / 24))
const daysToEnd = Math.ceil((hireEnd - new Date()) / (1000 * 60 * 60 * 24))
if (daysToEnd > 5) {
const earliest = new Date(hireEnd)
earliest.setDate(earliest.getDate() - 5)
return (
<div className="alert alert-warn">
Payment can't be taken this far ahead — the hold on your card would expire before your hire ends. You can pay from <strong>{earliest.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>.
</div>
)
}
return (
<Elements stripe={stripePromise} options={{ clientSecret }}>
<CheckoutForm
booking={booking}
clientSecret={clientSecret}
onSuccess={() => setSuccess(true)}
/>
</Elements>
)
})()}
</div>
</div>
</div>
)
}